export const isImageData = (data: unknown) => typeof data === 'string' && data.startsWith('data:image/');

export const downloadBlob = (name: string, blob: Blob, type: string) => {
    const link = document.createElement('a');
    const href = window.URL.createObjectURL(blob);

    link.download = name;
    link.href = href;
    link.dataset.downloadurl = [type, link.download, link.href].join(':');

    const downloadEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true,
    });

    link.dispatchEvent(downloadEvent);
    link.remove();
    window.URL.revokeObjectURL(href);
};

export const nextChar = (c: string) => {
    return String.fromCharCode(c.charCodeAt(0) + 1);
};

export function insertUrlParam(paramList: { key: string, value: string }[], type: 'add' | 'replace' = 'add') {
    if (window.history.pushState) {
        const searchParams = new URLSearchParams(type === 'add'
            ? window.location.search
            : '');
        paramList.forEach(({ key, value }) => searchParams.set(key, value));
        const newurl = window.location.protocol
            + '//'
            + window.location.host
            + window.location.pathname
            + '?'
            + searchParams.toString();
        window.history.pushState({ path: newurl }, '', newurl);
    }
};

export const setCharAt = (str: string, index: number, chr: string) => {
    if (index > str.length - 1) return str;
    return str.substring(0, index) + chr + str.substring(index + 1);
};

export const filterFromTrail = (list: (string | undefined | null)[]) => {
    const nextList = [...list];
    for (let cnt = list.length - 1; cnt >= 0; cnt--) {
        if (nextList[cnt] == null || nextList[cnt] === '') {
            nextList.pop();
        } else {
            break;
        }
    }

    return nextList;
};

export const padRight = <Value extends (string | undefined | null)[]>(list: Value, limit: number, padUnit = '') => {
    const nextList = [...list];
    for (let cnt = list.length; cnt < limit; cnt++) {
        nextList.push(padUnit);
    }

    return nextList;
};

export const isJsonObjectEqual = (payload1: unknown, payload2: unknown, loose = false) => {
    /**
     * Check `array`, `object` và `null`
     */
    if (typeof payload1 === 'object' && typeof payload2 === 'object') {
        if (payload1 === null || payload2 === null) return payload1 === payload2;

        if (Array.isArray(payload1) && Array.isArray(payload2)) {
            if (payload1.length !== payload2.length) return false;

            for (let cnt = 0; cnt < payload1.length; cnt++) {
                if (isJsonObjectEqual(payload1[cnt], payload2[cnt], loose) === false) return false;
            }
            return true;
        }

        if (payload1.toString() === '[object Object]' && payload2.toString() === '[object Object]') {
            if (Object.keys(payload1).length !== Object.keys(payload2).length) return false;

            for (const key of Object.keys(payload1)) {
                if (isJsonObjectEqual(
                    payload1[key as keyof typeof payload1],
                    payload2[key as keyof typeof payload2],
                    loose,
                ) === false) return false;
            }
            return true;
        }

        if (loose) return `${payload1}` === `${payload2}`;
        else return payload1 === payload2;
    }
    /**
     * Check `number` và `string`
     */
    return payload1 === payload2;
};

type DocumentWithOutdatedFeature = Document & {
    selection: {
        createRange: () => ({
            text: string,
        }),
    },
};
export function insertAtCursor(target: HTMLTextAreaElement, myValue: string) {
    //IE support
    if ((document as DocumentWithOutdatedFeature).selection) {
        target.focus();
        const sel = (document as DocumentWithOutdatedFeature).selection.createRange();
        sel.text = myValue;

        return {
            value: target.value,
            position: 0,
        };
    }
    //MOZILLA and others
    else if (target.selectionStart || target.selectionStart === 0) {
        const startPos = target.selectionStart;
        const endPos = target.selectionEnd;
        target.value = target.value.substring(0, startPos)
            + myValue
            + target.value.substring(endPos, target.value.length);
        target.selectionStart = startPos + myValue.length;
        target.selectionEnd = startPos + myValue.length;
        target.focus();

        return {
            value: target.value,
            position: startPos + myValue.length,
        };
    } else {
        target.value += myValue;

        return {
            value: target.value,
            position: 0,
        };
    }
};

export async function parallelLimit<T>(
    tasks: (() => Promise<T>)[],
    maxConcurrent: number
): Promise<T[]> {
    const results: T[] = new Array(tasks.length);
    let nextIndex = 0;

    async function worker(): Promise<void> {
        while (nextIndex < tasks.length) {
            const index = nextIndex++;
            results[index] = await tasks[index]();
        }
    }

    const workers = Array.from(
        { length: Math.min(maxConcurrent, tasks.length) },
        () => worker()
    );

    await Promise.all(workers);
    return results;
};

export function capitalizeFirstLetter(str: string) {
    if (!str) return ''; // Handle empty strings safely
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export function updateCropRatio(
    crop: Partial<ReactCrop.Crop>,
    image: { width: number, height: number },
    r: number,
): Partial<ReactCrop.Crop> {
    if (typeof crop.width !== 'number' || typeof crop.y !== 'number' || typeof crop.x !== 'number') {
        return crop;
    }
    // The crop percentage ratio that produces the target pixel ratio r.
    // Derivation: (w% * imgW) / (h% * imgH) = r  =>  w%/h% = r * imgH / imgW
    const cropRatio = r * image.height / image.width;

    // Try to keep width — derive height from it
    const heightFromWidth = crop.width / cropRatio;
    if (crop.y + heightFromWidth <= 100) {
        return { ...crop, height: heightFromWidth };
    }

    // Try to keep y — clamp height, derive width from clamped height
    const maxHeightFromY = 100 - crop.y;
    const widthFromY = maxHeightFromY * cropRatio;
    if (crop.x + widthFromY <= 100) {
        return { ...crop, width: widthFromY, height: maxHeightFromY };
    }

    // Try to keep x — reset y to 0, fit largest possible crop from x
    const maxWidth = 100 - crop.x;
    const maxHeight = 100;
    const widthFromX = Math.min(maxWidth, maxHeight * cropRatio);
    const heightFromX = widthFromX / cropRatio;
    return { ...crop, y: 0, width: widthFromX, height: heightFromX };
};

export const getLocalSetting = () => {
    let setting: Record<string, unknown> = {};
    try {
        const cachedStore = JSON.parse(localStorage.getItem('setting') ?? '{}');

        if (cachedStore && typeof cachedStore === 'object' && !Array.isArray(cachedStore)) setting = cachedStore;
    } catch (e) {
        console.error('useSetting: Error when getting cached setting', e);
    }

    return setting;
};